import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getDefaultEnabledActions } from '../lib/commandActions'

interface NotificationPrefs {
  emailAppointments: boolean
  emailReminders: boolean
  emailBilling: boolean
  pushAppointments: boolean
  pushReminders: boolean
  pushSystem: boolean
}

interface CalendarPrefs {
  defaultView: 'day' | 'week' | 'month'
  defaultSlotDuration: number
  startHour: number
  endHour: number
  showWeekends: boolean
}

interface SettingsState {
  language: string
  timezone: string
  dateFormat: string
  timeFormat: '12h' | '24h'
  compactMode: boolean
  notifications: NotificationPrefs
  calendar: CalendarPrefs
  enabledCommandActions: string[]
  setLanguage: (lang: string) => void
  setTimezone: (tz: string) => void
  setDateFormat: (format: string) => void
  setTimeFormat: (format: '12h' | '24h') => void
  setCompactMode: (compact: boolean) => void
  setNotificationPref: <K extends keyof NotificationPrefs>(key: K, value: boolean) => void
  setCalendarPref: <K extends keyof CalendarPrefs>(key: K, value: CalendarPrefs[K]) => void
  toggleCommandAction: (id: string) => void
  resetToDefaults: () => void
}

const defaultNotifications: NotificationPrefs = {
  emailAppointments: true,
  emailReminders: true,
  emailBilling: true,
  pushAppointments: true,
  pushReminders: false,
  pushSystem: true,
}

const defaultCalendar: CalendarPrefs = {
  defaultView: 'week',
  defaultSlotDuration: 30,
  startHour: 8,
  endHour: 20,
  showWeekends: false,
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      language: 'es-ES',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateFormat: 'dd/MM/yyyy',
      timeFormat: '24h',
      compactMode: false,
      notifications: { ...defaultNotifications },
      calendar: { ...defaultCalendar },
      enabledCommandActions: getDefaultEnabledActions(),
      setLanguage: (language) => set({ language }),
      setTimezone: (timezone) => set({ timezone }),
      setDateFormat: (dateFormat) => set({ dateFormat }),
      setTimeFormat: (timeFormat) => set({ timeFormat }),
      setCompactMode: (compactMode) => set({ compactMode }),
      setNotificationPref: (key, value) =>
        set((state) => ({
          notifications: { ...state.notifications, [key]: value },
        })),
      setCalendarPref: (key, value) =>
        set((state) => ({
          calendar: { ...state.calendar, [key]: value },
        })),
      toggleCommandAction: (id) =>
        set((state) => {
          const enabled = new Set(state.enabledCommandActions)
          if (enabled.has(id)) {
            enabled.delete(id)
          } else {
            enabled.add(id)
          }
          return { enabledCommandActions: Array.from(enabled) }
        }),
      resetToDefaults: () =>
        set({
          language: 'es-ES',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          dateFormat: 'dd/MM/yyyy',
          timeFormat: '24h',
          compactMode: false,
          notifications: { ...defaultNotifications },
          calendar: { ...defaultCalendar },
          enabledCommandActions: getDefaultEnabledActions(),
        }),
    }),
    {
      name: 'smartdental-settings',
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const all = new Set(getDefaultEnabledActions())
        const current = new Set(state.enabledCommandActions)
        // Auto-enable any new actions that were added since last save
        let changed = false
        for (const id of all) {
          if (!current.has(id)) {
            current.add(id)
            changed = true
          }
        }
        if (changed) {
          state.enabledCommandActions = Array.from(current)
        }
      },
    }
  )
)
